import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Tooltip,
  Modal,
  Form,
  Select,
  Divider,
  Checkbox,
  message,
  Spin,
} from 'antd';
import { useInvoiceEmailSender } from '../../hooks/invoice/useInvoiceEmailSender';
import { useUsersDropdownList } from '../../hooks/users/useUsersDropdownList';

const MODULE_ACCESS_OPTIONS = [
  { value: 'CASE', label: 'CASE' },
  { value: 'ACCOUNT', label: 'ACCOUNT' },
  { value: 'REPORT', label: 'REPORT' },
];

export const EmailSenderComponent = ({
  title,
  placement,
  loading,
  onClick,
}) => {
  return (
    <Tooltip title={title} placement={placement}>
      <Button loading={loading} onClick={onClick} icon={<i className="fa fa-envelope" />}>
      </Button>
    </Tooltip>
  );
};

EmailSenderComponent.propTypes = {
  title: PropTypes.string.isRequired,
  placement: PropTypes.string,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
};

const InvoiceEmailSender = ({ invoiceId, onEmailSent, clinicLocationId }) => {
  const { loading, sentInvoiceEmail } = useInvoiceEmailSender();
  const {
    data: usersDropdownList,
    getUsersList,
    loading: usersDropdownListLoading,
  } = useUsersDropdownList();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const fetchUsersDropdownList = async () => {
    if (!clinicLocationId) return;
    await getUsersList({
      clinicLocationId,
      limit: 1000,
      page: 1,
    });
  };
  const showModal = () => {
    fetchUsersDropdownList();
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = async (values) => {
    // Check if any one of sendTo or moduleAccess is provided
    if (!values.sendTo?.length && !values.moduleAccess?.length) {
      message.error(
        'Please provide either email address or users with module access'
      );
      return;
    }
    await sentInvoiceEmail(invoiceId, {
      sendTo: values.sendTo,
      moduleAccess: values.moduleAccess,
      withInvoice: values.withInvoice,
    });
    handleCancel();
    onEmailSent?.();
  };

  return (
    <>
      <EmailSenderComponent
        title="Send Email"
        placement="left"
        loading={loading}
        onClick={showModal}
      />
      <Modal
        title="Send Invoice Email"
        open={isModalOpen}
        onOk={form.submit}
        onCancel={handleCancel}
        afterClose={() => form.resetFields()}
        confirmLoading={loading}
      >
        <Spin spinning={usersDropdownListLoading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ withInvoice: true }}
          >
            <Form.Item label="Send To" name="sendTo">
              <Select
                mode="tags"
                placeholder="Enter email address"
                options={usersDropdownList?.items}
                optionRender={(option) => (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <span>{option.value}</span>
                    {option.data?.description ? (
                      <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                        {option.data.description}
                      </span>
                    ) : null}
                  </div>
                )}
              />
            </Form.Item>
            <Divider>And / Or</Divider>
            <Form.Item label="Users With Module Access" name="moduleAccess">
              <Select
                mode="multiple"
                placeholder="Select module access"
                options={MODULE_ACCESS_OPTIONS}
              />
            </Form.Item>
            <Form.Item
              name="withInvoice"
              valuePropName="checked"
              extra="If enabled, the invoice PDF will be generated and attached to this email."
            >
              <Checkbox>Attach invoice PDF to email</Checkbox>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

InvoiceEmailSender.propTypes = {
  invoiceId: PropTypes.number,
  invoiceNo: PropTypes.string,
  onEmailSent: PropTypes.func,
  clinicLocationId: PropTypes.number,
};

export default InvoiceEmailSender;
